<script setup>
import { computed } from 'vue';

const props = defineProps({
  card: { type: Object, required: true },
  stages: { type: Array, required: true },
});

const emit = defineEmits(['move']);

const currentIndex = computed(() =>
  props.stages.findIndex(stage => stage.id === props.card.stage_id)
);

// The status is not a step: a card closed on "Agendada" shows the steps it really reached.
const stateOf = index => {
  if (index < currentIndex.value) return 'done';
  if (index === currentIndex.value) return 'current';
  return 'upcoming';
};
</script>

<template>
  <ol class="flex mb-0 list-none ltr:pl-0 rtl:pr-0">
    <li
      v-for="(stage, index) in stages"
      :key="stage.id"
      class="relative flex flex-col items-center flex-1 min-w-0 gap-2"
      :data-stage-state="stateOf(index)"
    >
      <!-- Connector from the previous stage: solid once reached, dotted ahead. -->
      <span
        v-if="index > 0"
        class="absolute top-2.5 ltr:right-1/2 rtl:left-1/2 w-full"
        :class="
          stateOf(index) === 'upcoming'
            ? 'border-t-2 border-dotted border-n-slate-6'
            : 'h-0.5 bg-n-teal-9'
        "
      />
      <button
        type="button"
        class="relative z-10 grid rounded-full size-5 place-content-center"
        :class="{
          'bg-n-teal-9 text-white': stateOf(index) === 'done',
          'bg-n-background ring-2 ring-n-teal-9 ring-offset-2 ring-offset-n-background':
            stateOf(index) === 'current',
          'bg-n-background': stateOf(index) === 'upcoming',
        }"
        :title="stage.name"
        :data-testid="`stage-tracker-${stage.id}`"
        @click="emit('move', stage.id)"
      >
        <span
          v-if="stateOf(index) === 'done'"
          class="i-lucide-check size-3.5"
        />
        <span
          v-else
          class="rounded-full size-2.5"
          :class="stateOf(index) === 'current' ? 'bg-n-teal-9' : 'bg-n-slate-6'"
        />
      </button>
      <span
        class="max-w-full px-1 text-xs text-center truncate"
        :class="
          stateOf(index) === 'upcoming'
            ? 'text-n-slate-10'
            : 'text-n-slate-12 font-medium'
        "
      >
        {{ stage.name }}
      </span>
    </li>
  </ol>
</template>
